import React, { useEffect, useState } from "react";
import axios from "axios";
import AdminLayout from "./adminLayout";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const API_URL = process.env.REACT_APP_API_URL;

const OrderDetails = () => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("regular"); // "regular" or "guest"
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  });

  const fetchOrders = async (page = 1, orderType = activeTab) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token not found. Please log in again.");
      }

      const endpoint = orderType === "regular" 
        ? `${API_URL}/api/orders/admin/allorders` 
        : `${API_URL}/api/guest-orders/all`;

      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        params: {
          page: page,
          limit: 10,
        },
      });

      console.log(response.data);
      
      // Handle different response structures for regular and guest orders
      let ordersData = [];
      let currentPage = 1;
      let totalPages = 1;
      let totalItems = 0;
      
      if (orderType === "regular") {
        // Regular orders structure
        ordersData = response.data.orders || [];
        currentPage = response.data.currentPage || 1;
        totalPages = response.data.totalPages || 1;
        totalItems = response.data.totalItems || 0;
      } else {
        // Guest orders structure
        ordersData = response.data.guestOrders || [];
        currentPage = response.data.currentPage || 1;
        totalPages = response.data.totalPages || 1;
        totalItems = response.data.totalItems || 0;
      }

      // Clear selected order when switching tabs
      setSelectedOrder(null);
      setOrders(ordersData);
      console.log(ordersData);
      
      setPagination({ currentPage, totalPages, totalItems });
      setError(null);
    } catch (error) {
      console.error("Error fetching orders:", error.message);
      setError("Failed to load orders. Please try again.");
      setOrders([]); // Reset orders on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial fetch on component mount
    fetchOrders(1, activeTab);
    
    // Cleanup function to handle component unmount
    return () => {
      // Any cleanup needed
    };
  }, []); // Only run on mount, tab changes are handled by handleTabChange

  const handlePageChange = (newPage) => {
    fetchOrders(newPage, activeTab);
  };

  const handleTabChange = (tab) => {
    if (tab === activeTab) return; // Prevent unnecessary re-fetching if already on this tab
    
    setActiveTab(tab);
    setPagination({
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
    });
    // Fetch the orders with the new tab type and reset to page 1
    fetchOrders(1, tab);
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "PAID":
        return "bg-green-100 text-green-700";
      case "PENDING":
        return "bg-yellow-100 text-yellow-700";
      case "FAILED":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  // Helper function to display product names in a compact format
  const getProductSummary = (products) => {
    if (!products || !Array.isArray(products) || products.length === 0) return "No products";

    // Get the first two product names, ensuring we handle undefined values
    const firstProducts = products.slice(0, 2)
      .map(p => (p && p.productName) ? p.productName : "Unknown product")
      .filter(Boolean);

    // If there are more than 2 products, add "and X more"
    if (products.length > 2) {
      return `${firstProducts.join(", ")} and ${products.length - 2} more`;
    }

    return firstProducts.join(", ");
  };

  // Helper function to normalize order ID based on order type
  const getOrderId = (order) => {
    // If orderId exists and is not null, use it, otherwise fall back to _id or id
    return order.orderId || order._id || order.id || "Unknown ID";
  };

const generateInvoice = async (order) => {
  // Determine if this is a guest order by checking for firstName/lastName instead of fullName
  const isGuestOrder = !order.address?.fullName && (order.address?.firstName || order.address?.lastName);
  
  // Handle name format based on order type
  let customerName = 'N/A';
  if (isGuestOrder) {
    customerName = `${order.address?.firstName || ''} ${order.address?.lastName || ''}`.trim();
  } else {
    customerName = order.address?.fullName || 'N/A';
  }
  
  // Handle address format based on order type
  let customerAddress = 'N/A';
  if (isGuestOrder) {
    customerAddress = order.address?.address || 'N/A';
  } else {
    customerAddress = order.address?.addressLine || order.address?.street || 'N/A';
  }
  
  const invoice = document.createElement("div");
  invoice.innerHTML = `
    <div style="padding: 20px; font-family: Arial, sans-serif; width: 1200px; margin: 0 auto; font-size: 18px; line-height: 1.5;">
      <div style="display: flex; justify-content: space-between; align-items: center;">
        <div style="border-radius: 50%; border: 2px solid #4ade80; width: 120px; height: 120px; overflow: hidden; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); background: #ffffff;">
          <img src="/logo.jpg" alt="PlantKart Logo" style="width: 100%; height: 100%; object-fit: cover;">
        </div>
        <div style="text-align: right;">
          <p><strong>Issued Date:</strong> ${new Date(order.createdAt).toLocaleDateString()}</p>
        </div>
      </div>
      <h1 style="text-align: center; margin: 30px 0; font-size: 40px; font-weight: 800; color: #111827;">INVOICE</h1>
      <div style="margin-bottom: 30px;">
        <h3 style="font-weight: 700; font-size: 22px; color: #111827;">Bill To:</h3>
        <p><strong>Name:</strong> ${customerName}</p>
        <p><strong>Phone:</strong> ${order.address?.phoneNumber || 'N/A'}</p>
        <p><strong>Email:</strong> ${order.address?.email || 'N/A'}</p>
      </div>
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
        <thead style="text-align: left; border-bottom: 2px solid #000;">
          <tr>
            <th style="padding: 12px 0;">Description</th>
            <th style="padding: 12px 0;">Price</th>
            <th style="padding: 12px 0;">Subtotal</th>
          </tr>
        </thead>
        <tbody>
          ${order.products?.map(product => `
            <tr>
              <td style="padding: 12px 0;">${product.quantity} x ${product.productName || 'Product'}</td>
              <td style="padding: 12px 0;">₹${product.price?.toFixed(2) || '0.00'}</td>
              <td style="padding: 12px 0;">₹${((product.price || 0) * (product.quantity || 1)).toFixed(2)}</td>
            </tr>
          `).join("") || '<tr><td colspan="3">No product details available</td></tr>'}
          <tr>
            <td></td>
            <td style="padding: 12px 0; text-align: right;"><strong>Total:</strong></td>
            <td style="padding: 12px 0;"><strong>₹${order.totalAmount?.toFixed(2) || '0.00'}</strong></td>
          </tr>
        </tbody>
      </table>
      <hr style="margin: 30px 0;">
      <div style="display: flex; justify-content: space-between; margin-top: 20px;">
        <div style="width: 48%;">
          <h3 style="font-weight: 700; font-size: 22px; color: #111827;">Payment Info</h3>
          <p><strong>Order ID:</strong> ${getOrderId(order)}</p>
          <p><strong>Payment ID:</strong> ${order.paymentId || order.stripeSessionId || 'N/A'}</p>
          <p><strong>Order Date:</strong> ${new Date(order.createdAt).toLocaleString()}</p>
          <p><strong>Status:</strong> ${order.status || 'N/A'}</p>
          <p><strong>Amount Paid:</strong> ₹${order.totalAmount?.toFixed(2) || '0.00'}</p>
        </div>
        <div style="width: 35%; margin-left: auto; text-align: right;">
          <h3 style="font-weight: 700; font-size: 22px; color: #111827;">Billing Address</h3>
          <p><strong>Name:</strong> ${customerName}</p>
          <p><strong>Phone:</strong> ${order.address?.phoneNumber || 'N/A'}</p>
          <p><strong>Address:</strong> ${customerAddress}</p>
          <p><strong>Landmark:</strong> ${order.address?.landmark || 'N/A'}</p>
          <p><strong>Pincode:</strong> ${order.address?.pincode || order.address?.zip || 'N/A'}</p>
          <p><strong>City:</strong> ${order.address?.city || 'N/A'}</p>
          <p><strong>State:</strong> ${order.address?.state || 'N/A'}</p>
          <p><strong>Country:</strong> ${order.address?.country || 'N/A'}</p>
        </div>
      </div>
      <p style="text-align: center; font-size: 20px; margin-top: 40px;">Thank you for shopping with us!</p>
    </div>`;

  document.body.appendChild(invoice);

  await html2canvas(invoice, { scale: 2 }).then((canvas) => {
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "pt", "a4");
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save(`Invoice_${getOrderId(order)}.pdf`);
  });

  document.body.removeChild(invoice);
};

  if (loading && orders.length === 0) {
    return (
      <AdminLayout>
        <div className="p-6 mt-12 flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <>
     {selectedOrder && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
    <div className="bg-white w-full max-w-2xl p-6 rounded-lg shadow-lg relative max-h-[90vh] overflow-y-auto">
      <button
        className="absolute top-4 right-4 text-gray-500 hover:text-red-600 text-xl font-bold"
        onClick={() => setSelectedOrder(null)}
      >
        ×
      </button>
      <h2 className="text-2xl font-bold mb-2">Order #{getOrderId(selectedOrder)}</h2>
      <p className="text-sm text-gray-500 mb-4">
        Placed on {new Date(selectedOrder.createdAt).toLocaleString()}
      </p>
      <div className="flex justify-between items-center mb-4">
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusClass(selectedOrder.status)}`}>
          {selectedOrder.status || 'Unknown'}
        </span>
        <span className="text-lg font-semibold text-green-600">
          ₹{selectedOrder.totalAmount?.toFixed(2) || '0.00'}
        </span>
      </div>
      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-2">Items</h3>
        {selectedOrder.products?.length > 0 ? (
          <div className="bg-gray-50 rounded-lg p-3">
            <table className="w-full">
              <thead>
                <tr className="text-sm text-gray-600">
                  <th className="text-left pb-2">Item</th>
                  <th className="text-center pb-2">Qty</th>
                  <th className="text-right pb-2">Price</th>
                  <th className="text-right pb-2">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {selectedOrder.products.map((product, index) => (
                  <tr key={index} className="border-t border-gray-200">
                    <td className="py-2 text-gray-800">{product.productName || 'Product'}</td>
                    <td className="py-2 text-center">{product.quantity}</td>
                    <td className="py-2 text-right">₹{product.price}</td>
                    <td className="py-2 text-right font-medium">₹{(product.quantity * product.price).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500">No item details available</p>
        )}
      </div>
      {selectedOrder.address && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-2">Delivery Address</h3>
          {/* Check if it's a guest order by looking for firstName/lastName instead of fullName */}
          {selectedOrder.address.firstName || selectedOrder.address.lastName ? (
            <>
              <p className="text-sm"><strong>Name:</strong> {`${selectedOrder.address.firstName || ''} ${selectedOrder.address.lastName || ''}`.trim()}</p>
              <p className="text-sm"><strong>Phone:</strong> {selectedOrder.address.phoneNumber || 'N/A'}</p>
              <p className="text-sm"><strong>Email:</strong> {selectedOrder.address.email || 'N/A'}</p>
              <p className="text-sm"><strong>Address:</strong> {selectedOrder.address.address || 'N/A'}</p>
              <p className="text-sm"><strong>Landmark:</strong> {selectedOrder.address.landmark || 'N/A'}</p>
              <p className="text-sm"><strong>Pincode:</strong> {selectedOrder.address.pincode || 'N/A'}</p>
              <p className="text-sm"><strong>City:</strong> {selectedOrder.address.city || 'N/A'}</p>
              <p className="text-sm"><strong>State:</strong> {selectedOrder.address.state || 'N/A'}</p>
              <p className="text-sm"><strong>Country:</strong> {selectedOrder.address.country || 'N/A'}</p>
            </>
          ) : (
            <>
              <p className="text-sm"><strong>Name:</strong> {selectedOrder.address.fullName || 'N/A'}</p>
              <p className="text-sm"><strong>Phone:</strong> {selectedOrder.address.phoneNumber || 'N/A'}</p>
              <p className="text-sm"><strong>Address:</strong> {selectedOrder.address.addressLine || selectedOrder.address.street || 'N/A'}</p>
              <p className="text-sm"><strong>Landmark:</strong> {selectedOrder.address.landmark || 'N/A'}</p>
              <p className="text-sm"><strong>Pincode:</strong> {selectedOrder.address.pincode || selectedOrder.address.zip || 'N/A'}</p>
              {selectedOrder.address.city && <p className="text-sm"><strong>City:</strong> {selectedOrder.address.city}</p>}
              {selectedOrder.address.state && <p className="text-sm"><strong>State:</strong> {selectedOrder.address.state}</p>}
            </>
          )}
        </div>
      )}
      <div className="text-sm text-gray-500 mt-4 border-t pt-3">
        {(selectedOrder.paymentId || selectedOrder.stripeSessionId) && (
          <p>
            <strong>Payment ID:</strong> {selectedOrder.paymentId || selectedOrder.stripeSessionId}
          </p>
        )}
        <p><strong>Order ID:</strong> {getOrderId(selectedOrder)}</p>
        <p><strong>Updated At:</strong> {new Date(selectedOrder.updatedAt || selectedOrder.createdAt).toLocaleString()}</p>
      </div>
      <div className="mt-6 text-right">
        <button
          onClick={() => generateInvoice(selectedOrder)}
          className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Download Invoice
        </button>
      </div>
    </div>
  </div>
)}

      <AdminLayout>
        <div className="p-6 mt-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-800 text-center">
            Order Management
          </h2>

          {/* Toggle Tabs */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex rounded-md shadow-sm" role="group">
              <button
                type="button"
                className={`px-5 py-2 text-sm font-medium border border-gray-200 rounded-l-lg ${
                  activeTab === "regular"
                    ? "bg-green-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
                onClick={() => handleTabChange("regular")}
              >
                Regular Orders
              </button>
              <button
                type="button"
                className={`px-5 py-2 text-sm font-medium border border-gray-200 rounded-r-lg ${
                  activeTab === "guest"
                    ? "bg-green-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
                onClick={() => handleTabChange("guest")}
              >
                Guest Orders
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
              {error}
            </div>
          )}

          <div className="overflow-x-auto bg-white shadow-lg rounded-lg">
            <table className="min-w-full border border-gray-300">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 border-b text-left text-sm font-semibold text-gray-700">Products</th>
                  <th className="px-4 py-3 border-b text-center text-sm font-semibold text-gray-700">Total Amount</th>
                  <th className="px-4 py-3 border-b text-center text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 border-b text-center text-sm font-semibold text-gray-700">Order Date</th>
                  <th className="px-4 py-3 border-b text-center text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.length > 0 ? (
                  orders.map((order) => (
                    <tr
                      key={order.id || order._id}
                      className="hover:bg-gray-50 transition duration-150"
                    >
                      <td className="px-4 py-3 border-b text-gray-800">
                        {order.products && Array.isArray(order.products) && order.products.length > 0 ? (
                          <div>
                            <p className="font-medium">{order.products.length} {order.products.length === 1 ? 'item' : 'items'}</p>
                            <p className="text-sm text-gray-600 truncate max-w-xs">
                              {getProductSummary(order.products)}
                            </p>
                          </div>
                        ) : (
                          <span className="text-gray-500">No details available</span>
                        )}
                      </td>
                      <td className="px-4 py-3 border-b text-center text-gray-800">
                        ₹{order.totalAmount ? order.totalAmount.toFixed(2) : '0.00'}
                      </td>
                      <td className="px-4 py-3 border-b text-center">
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusClass(order.status)}`}
                        >
                          {order.status || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3 border-b text-center text-gray-800">
                        {order.createdAt ? new Date(order.createdAt).toLocaleString() : 'Unknown date'}
                      </td>
                      <td className="px-4 py-3 border-b text-center">
                        <button
                          onClick={() => setSelectedOrder(order)}
                          className="text-blue-500 hover:text-blue-700 font-medium"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-4 py-3 text-center text-gray-500">
                      No orders found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="px-4 py-2 mx-1 bg-gray-200 text-gray-700 rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-4 py-2 mx-1 text-gray-700">
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="px-4 py-2 mx-1 bg-gray-200 text-gray-700 rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </AdminLayout>
    </>
  );
}

export default OrderDetails;